import { Theme, css } from '@emotion/react';
import React from 'react';

import { VisuallyHidden } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { mediaQueries } from '~src/theme/mediaQueries';

import { Price } from '../../api/api';
import { useGlobalState, useSessionState } from '../global-state';
import { formatCurrencyIsoCode, formatCurrencySymbol } from './currency';
import { priceToString } from './format';

function getPriceAmount(price: number | Price) {
  return typeof price === 'number' ? price : price.amount;
}

/**
 * "formatPrice" function with global flags included.
 */
export function useFormatPriceFn() {
  const session = useSessionState();
  const state = useGlobalState();
  const globalCurrency = state.purchase_currency;

  return (price: number | Price) => {
    const amount = getPriceAmount(price);
    const currency = typeof price === 'number' ? globalCurrency : price.currency;
    const amountStr = priceToString(amount);

    if (session.flags.display_currency_iso_code) {
      return formatCurrencyIsoCode(amountStr, currency, session.flags.show_currency_before_price);
    }

    return formatCurrencySymbol(amountStr, currency);
  };
}

interface PriceComponentProps {
  showPlus?: boolean;
  value: number | Price;
}

/**
 * Price component has no styling, it is suitable to take any font styling from
 * parent.
 */
export const PriceComponent: React.FC<PriceComponentProps> = ({ showPlus, value, ...props }) => {
  const { tICU } = useTranslations();
  const session = useSessionState();
  const formatFn = useFormatPriceFn();

  const amount = getPriceAmount(value);

  const showFreeDeliveryMessage = amount === 0 && !session.flags.enable_price_zero_value;

  const priceText =
    // @ts-ignore TODO: this flag should be added by backend
    showFreeDeliveryMessage
      ? tICU('widget.PRICE_FREE')
      : `${showPlus ? '+' : ''}${formatFn(value)}`;

  return (
    <span data-test="price" {...props}>
      {priceText}
    </span>
  );
};

interface CompoundPriceComponentProps {
  basePrice?: number | Price;
  netPrice?: number | Price;
  price: number | Price;
  enableImperfectLabels?: boolean;
}

export const CompoundPriceComponent: React.FC<CompoundPriceComponentProps> = ({
  basePrice,
  price,
  netPrice,
  enableImperfectLabels,
}) => {
  const { tICU } = useTranslations();
  const session = useSessionState();

  /**
   * This seems a valid condition for showing base price all over the app,
   * regadless whether it is a category or a free shipping indicator.
   *
   * Note: This feature was initially triggered by
   * enable_free_shipping_indicator flag. Later a separate show_base_price flag
   * was added only for this purpose.
   */
  const enableBase = session.flags.show_base_price || session.flags.enable_free_shipping_indicator;

  if (!enableBase && netPrice) {
    return (
      <div>
        <VisuallyHidden>{tICU('widget.aria.CURRENT_PRICE')}</VisuallyHidden>
        <PriceComponent css={styles.regular} value={netPrice} />
      </div>
    );
  }

  const showBase =
    enableBase &&
    basePrice !== undefined &&
    getPriceAmount(basePrice) > getPriceAmount(price) &&
    getPriceAmount(basePrice) !== 0;

  /**
   * Note: the "use_price_from" implementation was removed because no one used it.
   */

  return (
    <div css={styles.priceWrapper}>
      {showBase && (
        <>
          <VisuallyHidden>
            {enableImperfectLabels
              ? tICU('widget.aria.CURRENT_PRICE')
              : tICU('widget.aria.PRICE_BEFORE_DISCOUNT')}
          </VisuallyHidden>
          <PriceComponent data-test="old-price" css={styles.base} value={basePrice!} />
        </>
      )}
      <>
        <VisuallyHidden>{tICU('widget.aria.PRICE_AFTER_DISCOUNT')}</VisuallyHidden>
        <PriceComponent css={styles.regular} value={price} />
      </>
    </div>
  );
};

const styles = {
  base: (theme: Theme) => ({
    color: theme.neutral35,
    fontVariationSettings: '"wght" 400',
    textDecoration: 'line-through',
    marginRight: '4px',
  }),
  regular: {
    fontVariationSettings: '"wght" 500',
  },
  priceWrapper: css({
    [mediaQueries.S]: {
      display: 'flex',
      flexDirection: 'column',
    },
  }),
};
